import { type } from "arktype";
import { Ceiling, ConfidenceBand, Derived, EvidenceLevel } from "./layers";
import { Adaptation, BlockKind, DismissReason, MovementPattern } from "./vocabulary";

// §8.2 adaptation inference — interpretation.
export const AdaptationHypothesis = type({
  "...": Derived,
  id: "string",
  clientId: "string",
  adaptation: Adaptation,
  rank: "'primary' | 'secondary'",
  confidence: ConfidenceBand,
  evidenceSummary: "string",
});
export type AdaptationHypothesis = typeof AdaptationHypothesis.infer;

export const EvidenceBasis = type({
  level: EvidenceLevel,
  ceiling: Ceiling,
  reasons: "string[]",
  factRefs: type({ kind: "string", id: "string" }).array(),
  signalRefs: type({ kind: "string", id: "string" }).array(),
  openQuestionIds: "string[]",
  comparableObservations: "number",
});
export type EvidenceBasis = typeof EvidenceBasis.infer;

export const Alternative = type({
  exerciseId: "string | null",
  exerciseText: "string",
  reason: "'equipment' | 'preference' | 'skill' | 'pain' | 'poor_prior_response' | 'variation'",
});
export type Alternative = typeof Alternative.infer;

export const Prescription = type({
  sets: "number | null",
  "reps?": "number | null",
  "timeSeconds?": "number | null",
  "loadKg?": "number | null",
  "loadText?": "string | null",
  "intensity?": "string | null",
  "restSeconds?": "number | null",
  "tempo?": "string | null",
  ceiling: Ceiling,
  "ceilingNote?": "string | null",
});
export type Prescription = typeof Prescription.infer;

export const PlannedExercise = type({
  id: "string",
  exerciseId: "string | null",
  exerciseText: "string",
  pattern: MovementPattern,
  prescription: Prescription,
  alternatives: Alternative.array(),
  "successCriterion?": "string",
  "rationale?": "string",
  "cues?": "string[]",
});
export type PlannedExercise = typeof PlannedExercise.infer;

export const PlanBlock = type({
  id: "string",
  kind: BlockKind,
  exercises: PlannedExercise.array(),
  "rationale?": "string",
});
export type PlanBlock = typeof PlanBlock.infer;

export const VariationExplanation = type({
  window: "number",
  recentPatterns: type({ pattern: MovementPattern, adaptation: Adaptation, sessionId: "string", at: "string" }).array(),
  changed: type.enumerated("movement_pattern", "adaptation_emphasis", "loading_scheme", "plane_or_stance", "modality").array(),
  repeatsPrevious: "boolean",
  explanation: "string",
});
export type VariationExplanation = typeof VariationExplanation.infer;

// R-REC-01..04
export const SessionPlan = type({
  "...": Derived,
  id: "string",
  clientId: "string",
  title: "string",
  intent: { primary: Adaptation, "secondary?": Adaptation },
  blocks: PlanBlock.array(),
  successCriteria: "string[]",
  rationale: "string",
  evidence: EvidenceBasis,
  variation: VariationExplanation,
  alternativeFramework: { name: "string", summary: "string", "plan?": "unknown" },
  "framework?": "string",
  "methodId?": "string | null",
  "adopted?": "boolean",
  "coachEdits?": "unknown[]",
});
export type SessionPlan = typeof SessionPlan.infer;

/** The model step's raw contract for the recommend workflow (before ceiling enforcement). */
export const ModelSessionProposal = type({
  title: "string",
  intent: { primary: Adaptation, "secondary?": Adaptation },
  blocks: type({
    kind: BlockKind,
    "rationale?": "string",
    exercises: type({
      exerciseText: "string",
      pattern: MovementPattern,
      sets: "number | null",
      "reps?": "number | null",
      "timeSeconds?": "number | null",
      "loadKg?": "number | null",
      "restSeconds?": "number | null",
      "tempo?": "string | null",
      alternatives: type({ exerciseText: "string", reason: "'equipment' | 'preference' | 'skill' | 'pain' | 'poor_prior_response' | 'variation'" }).array(),
      "successCriterion?": "string",
      "rationale?": "string",
      "cues?": "string[]",
    }).array(),
  }).array(),
  successCriteria: "string[]",
  rationale: "string",
  citations: "string[]",
  alternativeFramework: { name: "string", summary: "string" },
});
export type ModelSessionProposal = typeof ModelSessionProposal.infer;

// R-REV-01..05
export const Review = type({
  "...": Derived,
  id: "string",
  sessionId: "string",
  clientId: "string",
  intent: { "primary?": Adaptation, "secondary?": Adaptation, text: "string" },
  meaningfulWork: type({ entryId: "string", label: "string", summary: "string", "bearsOn?": Adaptation }).array(),
  otherExposure: type({ entryId: "string", label: "string", summary: "string" }).array(),
  deviations: type({ deviationId: "string", text: "string", "whyQuestionId?": "string | null" }).array(),
  likelyAccomplished: type({ criterion: "string", met: "'met' | 'not_met' | 'unknown'", signal: "string", interpretation: "string" }).array(),
  implication: "string | null",
  selfReport: "boolean",
  weight: "number",
});
export type Review = typeof Review.infer;

export const ModelReviewProposal = type({
  meaningfulWork: type({ entryId: "string", summary: "string" }).array(),
  otherExposure: type({ entryId: "string", summary: "string" }).array(),
  likelyAccomplished: type({ criterion: "string", met: "'met' | 'not_met' | 'unknown'", signal: "string", interpretation: "string" }).array(),
  implication: "string | null",
  citations: "string[]",
});
export type ModelReviewProposal = typeof ModelReviewProposal.infer;

// §7.7 R-Q-01..05
export const Question = type({
  id: "string",
  clientId: "string",
  text: "string",
  addressee: "'trainer' | 'client'",
  settles: "string",
  defaultIfUnanswered: "string",
  "defaultBranch?": "'conservative'",
  riskRelated: "boolean",
  "options?": "string[]",
  attachedTo: type({ kind: "string", id: "string" }).array(),
  expiresAt: "string | null",
  status: "'open' | 'answered' | 'expired' | 'never_found_out'",
  "answer?": { text: "string", by: "'trainer' | 'client'", at: "string", provisional: "boolean" },
  createdAt: "string",
});
export type Question = typeof Question.infer;

// §7.6
export const Challenge = type({
  "...": Derived,
  id: "string",
  clientId: "string",
  kind: "'evidence_gap' | 'self_question' | 'adaptation_change' | 'failure_diagnosis'",
  questions: "string",
  whyNow: "string",
  settles: "string",
  actions: type.enumerated("add_to_today", "ask_client", "dismiss").array(),
  "alternativeView?": "string",
  "dismissal?": { reason: DismissReason, "text?": "string", at: "string", reasoned: "boolean" },
  suppressedUntil: "string | null",
});
export type Challenge = typeof Challenge.infer;

// §7.2.4
export const Consideration = type({
  "...": Derived,
  id: "string",
  clientId: "string",
  kind: "'variation' | 'staleness' | 'neglected_quality' | 'method' | 'goal_alignment'",
  noticed: "string",
  whyItMatters: "string",
  actions: type.enumerated("draft_session", "add_to_next_plan", "snooze", "dismiss").array(),
  "methodId?": "string | null",
  "draftPlanId?": "string | null",
  "dismissal?": { reason: DismissReason, "text?": "string", at: "string", reasoned: "boolean" },
  "snoozedUntil?": "string | null",
});
export type Consideration = typeof Consideration.infer;

export const Escalation = type({
  id: "string",
  clientId: "string",
  pattern: "string",
  advice: "string",
  raisedAt: "string",
  "resolvedAt?": "string | null",
});
export type Escalation = typeof Escalation.infer;

export const PoorResponse = type({
  id: "string", clientId: "string", exerciseId: "string", reason: "string", observedAt: "string", effectiveAt: "string",
});
export type PoorResponse = typeof PoorResponse.infer;

export const ClientModelSignal = type({
  "...": Derived,
  id: "string",
  clientId: "string",
  key: type.enumerated("tolerated_volume", "tissue_tolerance", "pain_response", "recovery", "confidence", "adherence", "exercise_response", "progression_readiness"),
  "exerciseId?": "string | null",
  value: "string",
  weight: "number",
});
export type ClientModelSignal = typeof ClientModelSignal.infer;

export const CoachProfile = type({
  practiceId: "string",
  dismissals: type({ kind: "string", reason: DismissReason, count: "number" }).array(),
  neverProgrammed: { "patterns?": MovementPattern.array(), "adaptations?": Adaptation.array(), "methods?": "string[]" },
  suppressions: type({ topic: "string", until: "string | null" }).array(),
  editedRecommendations: "number",
});
export type CoachProfile = typeof CoachProfile.infer;
